/* Custom File */

import useAppSelector from "@insite/client-framework/Common/Hooks/useAppSelector";
import { useShellContext } from "@insite/client-framework/Components/IsInShell";
import Zone from "@insite/client-framework/Components/Zone";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import { BreakpointKey } from "@insite/mobius/utilities/breakpointMediaQueries";
import useExpandProps, { ExpandStyles } from "@store/Framework/Common/Hooks/useExpandProps";
import breakpointValues from "@store/Framework/Common/Utilities/breakpointValues";
import mergeBreakpointValues from "@store/Framework/Common/Utilities/mergeBreakpointValues";
import mergeProps from "@store/Framework/Common/Utilities/mergeProps";
import { pxToRemArray } from "@store/Framework/Common/Utilities/unitHelper";
import { useCurrentPage } from "@store/Framework/Store/Data/Pages/PageHooks";
import { CarouselArrowPresentationProps } from "@store/Library/Components/Toolkit/Carousel/CarouselArrow";
import { CarouselButtonPresentationProps } from "@store/Library/Components/Toolkit/Carousel/CarouselButton";
import Carousel, {
    carouselDefaultCustomCss,
    CarouselPresentationProps,
    CarouselSlide,
} from "@store/Overrides/Components/Carousel";
import * as cssLinter from "css";
import React, { useMemo } from "react";
import { css } from "styled-components";

const buttonSize = pxToRemArray([32, null, null, null, 43]);
const controlsSpacing = pxToRemArray([15]);
const dotsSize = pxToRemArray([8, null, 16]);
const inlineArrowsBreakpoint: BreakpointKey = "sm";

const buttonProtectionSpacing = mergeBreakpointValues(buttonSize, controlsSpacing, (x, y) => `calc(${x} + ${y})`);
const dotProtectionSpacing = mergeBreakpointValues(dotsSize, controlsSpacing, (x, y) => `calc(${x} + ${y})`);

const carouselButtonProps: CarouselButtonPresentationProps = {
    size: buttonSize,
};

const carouselArrowProps: CarouselArrowPresentationProps = {
    extendedStyles: {
        carouselButton: carouselButtonProps,
    },
    positionOffset: pxToRemArray([5, null, null, null, 10]),
    positionOrigin: 0,
};

const enum fields {
    autoplay = "autoplay",
    autoplayDelay = "autoplayDelay",
    customCSS = "customCSS",
    items = "items",
    showArrows = "showArrows",
    showDots = "showDots",
}

const enum itemFields {
    id = "id",
    name = "name",
}

interface OwnProps extends WidgetProps, ExpandStyles<SlideshowStyles> {
    fields: {
        [fields.autoplay]: boolean;
        [fields.autoplayDelay]: number;
        [fields.customCSS]: string;
        [fields.items]: [
            {
                fields: {
                    [itemFields.id]: string;
                    [itemFields.name]: string;
                };
            },
        ];
        [fields.showArrows]: boolean;
        [fields.showDots]: boolean;
    };
}

export interface SlideshowStyles {
    carousel?: CarouselPresentationProps;
}

export const slideshowStyles: SlideshowStyles = {
    carousel: {
        extendedStyles: {
            carouselArrowPrevious: carouselArrowProps,
            carouselArrowNext: carouselArrowProps,
            carouselPause: carouselButtonProps,
            carouselPlay: carouselButtonProps,
            carouselControls: {
                css: css`
                    position: absolute;
                    ${breakpointValues(controlsSpacing, "bottom")}
                    margin: 0;
                `,
            },
            carouselDots: {
                variant: "outline",
                size: dotsSize,
            },
            carouselSlide: {
                css: css`
                    > div /* Zone */ {
                        height: 100%;

                        > [data-widget="Basic/Banner"] /* Preview Mode */ {
                            height: 100%;
                        }
                    }

                    .banner {
                        min-height: 100%;
                    }

                    .carousel.has-dot-controls && {
                        .banner-inner-wrapper {
                            ${breakpointValues(dotProtectionSpacing, "margin-bottom")}
                        }

                        > [class^="ZoneStyle"] > [class^="ZoneWrapper"] > [class^="AddContainer"] /* Edit Mode */ {
                            ${breakpointValues(dotProtectionSpacing, "padding-bottom")}
                        }
                    }

                    .carousel.has-button-controls && {
                        .banner-inner-wrapper {
                            ${breakpointValues(buttonProtectionSpacing, "margin-bottom")}
                        }

                        > [class^="ZoneStyle"] > [class^="ZoneWrapper"] > [class^="AddContainer"] /* Edit Mode */ {
                            ${breakpointValues(buttonProtectionSpacing, "padding-bottom")}
                        }
                    }

                    > [class^="ZoneStyle"] > [class^="ZoneWrapper"] /* Edit Mode */ {
                        height: 100%;

                        > [data-widget="Basic/Banner"] {
                            height: 100%;

                            > [class^="WidgetStyle"] {
                                height: 100%;

                                > [class^="WidgetWrapper"] {
                                    height: 100%;
                                }
                            }
                        }

                        > [class^="AddContainer"]:not(:first-child) {
                            display: none;
                        }
                    }
                `,
            },
        },
        inlineArrowsBreakpoint,
        itemsGap: 0,
    },
};

export const Slideshow = ({
    extendedStyles,
    fields: { autoplay, autoplayDelay, customCSS, items, showArrows, showDots },
    id,
    overridedStyles,
}: OwnProps) => {
    const currentPage = useCurrentPage();
    const { isEditing, pageId: shellPageId } = useShellContext();
    const styles = useExpandProps(slideshowStyles, extendedStyles, overridedStyles);

    const zoneNames: string[] = [];
    const slides: CarouselSlide[] = items.map((item, index) => {
        const zoneName = `Item-${item.fields.id || index}`;
        zoneNames.push(zoneName);

        return {
            id: `item-${id}-${item.fields.id || index}`,
            renderComponent: () => <Zone contentId={id} zoneName={zoneName} />,
        };
    });

    const zonesWidgets = useAppSelector(state => {
        const pageId = shellPageId || currentPage.id;
        const zones = state.data.pages.widgetIdsByPageIdParentIdAndZone[pageId]?.[id];

        return zoneNames.map(x => zones?.[x]?.map(y => state.data.pages.widgetsById[y]));
    });

    const carouselStyles = useMemo(() => {
        return mergeProps<SlideshowStyles["carousel"]>(styles.carousel, {
            extendedStyles: {
                wrapper: {
                    css: css`
                        ${customCSS}
                    `,
                },
            },
        });
    }, [customCSS]);

    
    const invertColorIndexes = zonesWidgets.reduce<number[]>((result, widget, index) => {
        return widget?.find(x => x.type === "Basic/Banner")?.fields.invertColor ? [...result, index] : result;
    }, []);

    if (!isEditing) {
        if (!slides.length) {
            return null;
        }

        if (slides.length === 1) {
            return (<Carousel
                {...carouselStyles}
                autoplay={autoplay}
                autoplayDelay={autoplayDelay * 1000}
                id={`slideshow-${id}`}
                invertColor={invertColorIndexes}
                itemsGap={0}
                showDots={showDots}
                showArrows={showArrows}
                slides={[slides[0]]}
            />)
        }
    }


    return (
        <Carousel
            {...carouselStyles}
            autoplay={autoplay}
            autoplayDelay={autoplayDelay * 1000}
            id={`slideshow-${id}`}
            invertColor={invertColorIndexes}
            itemsGap={0}
            showDots={showDots}
            showArrows={showArrows}
            slides={slides}
        />
    );
};

const defaultCustomCss = `&${carouselDefaultCustomCss}`;

const advancedTab = { displayName: "Advanced", sortOrder: 1 };

const widgetModule: WidgetModule<Toolkit.FieldDefinition> = {
    component: Slideshow,
    definition: {
        group: "Basic",
        icon: "presentation-screen",
        fieldDefinitions: [
            {
                name: fields.showArrows,
                displayName: "Show Arrows",
                editorTemplate: "CheckboxField",
                fieldType: "Contextual",
                defaultValue: true,
            },
            {
                name: fields.showDots,
                displayName: "Show Dots",
                editorTemplate: "CheckboxField",
                fieldType: "Contextual",
                defaultValue: true,
            },
            {
                name: fields.autoplay,
                displayName: "Enable autoplay",
                editorTemplate: "CheckboxField",
                fieldType: "Contextual",
                defaultValue: false,
            },
            {
                name: fields.autoplayDelay,
                displayName: "Autoplay delay",
                editorTemplate: "IntegerField",
                fieldType: "Contextual",
                defaultValue: 8,
                min: 1,
                isVisible: x => x.fields.autoplay,
                tooltip: `The number of seconds to wait before changing slide, autoplay will remain disabled while editing`,
                tooltipPosition: "left",
            },
            {
                name: fields.items,
                getDisplay: x => x.fields.name || translate("Item"),
                editorTemplate: "ListField",
                defaultValue: [],
                fieldType: "Contextual",
                fieldDefinitions: [
                    {
                        name: itemFields.id,
                        editorTemplate: "UniqueIdField",
                    },
                    {
                        name: itemFields.name,
                        displayName: "Name",
                        editorTemplate: "TextField",
                        defaultValue: "",
                    },
                ],
            },
            {
                name: fields.customCSS,
                displayName: "Custom CSS",
                editorTemplate: "CodeField",
                fieldType: "Contextual",
                tab: advancedTab,
                defaultValue: defaultCustomCss,
                isVisible: (item, shouldDisplayAdvancedFeatures) => !!shouldDisplayAdvancedFeatures,
                validate: value => {
                    if (value === undefined || value === null) {
                        return "";
                    }

                    const result = cssLinter.parse(value, { silent: true });
                    if (result?.stylesheet?.parsingErrors) {
                        // the error output at this time only has room for one line so we just show the first error
                        return result.stylesheet.parsingErrors.length <= 0
                            ? ""
                            : result.stylesheet.parsingErrors.map(error => `${error.reason} on line ${error.line}`)[0];
                    }

                    return "Unable to parse Css";
                },
                options: {
                    mode: "css",
                    lint: true,
                    autoRefresh: true,
                },
            },
        ],
    },
};

export default widgetModule;
